/**
 * Photo Enhance screen (photo_enhance tool).
 *
 * Flow: 1 Upload photo → 2 Choose enhance type → (3 Output size, HD Upscale
 * only) → Generate. The types are product features, not templates: the server
 * owns the wording and picks the engine (HD Upscale runs on a dedicated
 * upscaler, the rest on the image-edit model), so the app only sends
 * `options.enhanceType` and, for HD Upscale, `options.upscaleFactor`.
 * The price is data (GET /credits/pricing).
 */
import { ApiError, api } from '../api/client.js';
import { navigate } from '../router.js';
import { escapeHtml } from '../components/placeholder.js';
import { icon } from '../components/icon.js';
import { toast, toastApiError } from '../ui/toast.js';
import { creditsChanged, getBalance, refreshCredits } from '../state/credits.js';
import { requireAccount, requireSignIn } from '../state/gate.js';
import { withUploadGuide } from '../components/uploadGuide.js';
import { SAMPLE_PHOTOS } from '../content/samplePhotos.js';
import { ENHANCE_EXAMPLES } from '../content/enhanceExamples.js';
import type { CreditPricingConfig, EnhanceType, JobOptions, UploadFileMeta } from '@loomia/shared';

const TOOL = 'photo_enhance' as const;
const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const CLIENT_MAX_BYTES = 25 * 1024 * 1024;
/** HD Upscale limit (the server enforces it too). */
const UPSCALE_MAX_BYTES = 10 * 1024 * 1024;

const TYPES: ReadonlyArray<{ id: EnhanceType; label: string; icon: string; hint: string }> = [
  { id: 'auto', label: 'Auto Enhance', icon: 'auto_awesome', hint: 'Sharper detail with balanced light and color.' },
  { id: 'portrait', label: 'Portrait', icon: 'face_retouching_natural', hint: 'Clearer faces while keeping natural skin texture.' },
  { id: 'upscale', label: 'HD Upscale', icon: 'hd', hint: 'More resolution and fine detail. Photos up to 10 MB.' },
  { id: 'restore_colorize', label: 'Restore & Colorize', icon: 'photo_filter', hint: 'Repairs an old black and white photo and brings it to life in color. Colors are AI estimates.' },
  { id: 'restore', label: 'Restore', icon: 'history', hint: 'Repairs scratches, stains and fading in old photos. Faces are kept as they are.' },
  { id: 'colorize', label: 'Colorize', icon: 'palette', hint: 'Adds color to black and white photos. Colors are AI estimates and may differ from the real scene.' },
];

interface Photo {
  status: 'empty' | 'uploading' | 'ready' | 'error';
  progress: number;
  previewUrl: string | null;
  fileId: string | null;
  bytes: number;
  error: string | null;
}

let renderSeq = 0;
let prevCleanup: (() => void) | null = null;

function newId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `id-${Date.now()}-${Math.floor(Math.random() * 1e9)}`;
}

export function renderPhotoEnhance(root: HTMLElement, query: URLSearchParams): void {
  const seq = ++renderSeq;
  const alive = (): boolean => seq === renderSeq && root.isConnected;

  const load = async (): Promise<void> => {
    if (prevCleanup) {
      prevCleanup();
      prevCleanup = null;
    }
    root.innerHTML = '<div class="co"><div class="co-skel"></div></div>';
    const [pricingR] = await Promise.allSettled([api.getPricing(), refreshCredits().catch(() => null)]);
    if (!alive()) return;
    if (pricingR.status === 'rejected') {
      root.innerHTML = `
        <div class="co"><div class="co-state">${icon('cloud_off')}
          <p>Couldn't load Photo Enhance. Check your connection.</p>
          <button class="btn btn-secondary" data-act="retry">Try again</button></div></div>`;
      root.querySelector('[data-act="retry"]')?.addEventListener('click', () => void load());
      return;
    }
    mount(pricingR.value);
  };

  const mount = (pricing: CreditPricingConfig): void => {
    const wanted = query.get('type');
    let type: EnhanceType = TYPES.some((t) => t.id === wanted) ? (wanted as EnhanceType) : 'auto';
    let factor: 2 | 4 = 2;
    let generating = false;
    let photo: Photo = { status: 'empty', progress: 0, previewUrl: null, fileId: null, bytes: 0, error: null };

    const price = (): number => pricing.perTool[TOOL]?.credits ?? 0;
    const current = (): (typeof TYPES)[number] => TYPES.find((t) => t.id === type) ?? TYPES[0]!;

    const missing = (): string | null => {
      if (photo.status === 'uploading') return 'Uploading…';
      if (photo.status !== 'ready') return 'Add your photo first';
      if (type === 'upscale' && photo.bytes > UPSCALE_MAX_BYTES) return 'Photo too large for HD Upscale';
      return null;
    };

    const options = (): JobOptions => ({
      enhanceType: type,
      ...(type === 'upscale' ? { upscaleFactor: factor } : {}),
    });

    // -- Markup -----------------------------------------------------------

    const step = (n: number, title: string): string =>
      `<div class="co-step"><span class="co-num">${n}</span><h2>${title}</h2></div>`;

    const heroHtml = (): string => {
      const ex = ENHANCE_EXAMPLES.find((e) => e.type === type);
      if (!ex) return '';
      return `
        <div class="co-hero">
          <figure class="co-before"><img src="${escapeHtml(ex.before)}" alt="" /><figcaption>Before</figcaption></figure>
          <span class="co-arrow" aria-hidden="true">${icon('arrow_forward')}</span>
          <figure class="co-after"><img src="${escapeHtml(ex.after)}" alt="" /><figcaption>After</figcaption></figure>
        </div>`;
    };

    const photoHtml = (): string => {
      const samples =
        SAMPLE_PHOTOS.length === 0
          ? ''
          : `<div class="co-samples"><p class="co-sub">Try these photos</p><div class="co-samples-row">${SAMPLE_PHOTOS.map(
              (s, i) => `<button class="co-sample" data-sample="${i}" aria-label="${escapeHtml(s.label)}"><img src="${escapeHtml(s.url)}" alt="" loading="lazy" /></button>`,
            ).join('')}</div></div>`;
      const rowCls = `co-photo-row${SAMPLE_PHOTOS.length === 0 ? ' no-samples' : ''}`;
      if (photo.status === 'empty') {
        return `
          <div class="${rowCls}">
            <button class="co-drop" data-act="pick">
              ${icon('add_photo_alternate')}<strong>Add your photo</strong><span>JPG, PNG or WebP · up to 25 MB</span>
            </button>
            ${samples}
          </div>`;
      }
      const label =
        photo.status === 'uploading'
          ? `Uploading… ${Math.round(photo.progress * 100)}%`
          : photo.status === 'error'
            ? escapeHtml(photo.error ?? 'Upload failed')
            : '';
      return `
        <div class="${rowCls}">
          <div class="co-photo is-${photo.status}">
            ${photo.previewUrl ? `<img src="${escapeHtml(photo.previewUrl)}" alt="Your photo" />` : ''}
            ${photo.status === 'uploading' ? `<div class="co-bar"><span style="width:${Math.round(photo.progress * 100)}%"></span></div>` : ''}
            <div class="co-photo-side">
              ${label ? `<span class="co-photo-label" data-photo-label>${label}</span>` : ''}
              <button class="co-link" data-act="pick">${photo.status === 'error' ? 'Try again' : 'Change'}</button>
            </div>
          </div>
          ${samples}
        </div>`;
    };

    const typesHtml = (): string => `
      <div class="co-types" role="radiogroup" aria-label="Enhance type">
        ${TYPES.map(
          (t) => `
          <button class="co-type${t.id === type ? ' selected' : ''}" role="radio" aria-checked="${t.id === type}" data-type="${t.id}">
            <span class="co-type-ico">${icon(t.icon)}</span><span>${escapeHtml(t.label)}</span>
          </button>`,
        ).join('')}
      </div>
      <p class="co-sub co-type-hint">${escapeHtml(current().hint)}</p>`;

    const sizeHtml = (): string =>
      type !== 'upscale'
        ? ''
        : `
      ${step(3, 'Output size')}
      <div class="co-seg" role="radiogroup" aria-label="Output size">
        ${([2, 4] as const)
          .map(
            (f) => `<button class="co-seg-opt${f === factor ? ' selected' : ''}" role="radio" aria-checked="${f === factor}" data-factor="${f}">${f}×</button>`,
          )
          .join('')}
      </div>`;

    const footerHtml = (): string => {
      const c = price();
      const bal = getBalance();
      const short = bal !== null && bal < c ? c - bal : 0;
      const why = missing();
      const btn =
        short > 0 && !why
          ? '<button class="btn btn-primary btn-block" data-act="buy">Get more credits</button>'
          : `<button class="btn btn-primary btn-block" data-act="generate" ${generating ? 'disabled' : ''}>${
              generating ? 'Starting…' : why ? escapeHtml(why) : `Enhance Photo ${icon('toll', { fill: true })} ${c}`
            }</button>`;
      return `
        <div class="co-total${short > 0 ? ' is-short' : ''}">
          <span>Total</span><strong>${icon('toll', { fill: true })} ${c}</strong>
          <small>Failed runs are refunded automatically</small>
        </div>
        ${short > 0 ? `<p class="co-warn">You need ${icon('toll', { fill: true })} ${short} more credits</p>` : ''}
        ${btn}`;
    };

    const paint = (): void => {
      root.innerHTML = `
        <div class="co">
          <header class="co-head">
            <button class="co-back" data-act="back" aria-label="Back">${icon('arrow_back')}</button>
            <div class="co-head-text"><h1>AI Photo Enhance</h1><p>Make your photos clearer, sharper and more vibrant with AI.</p></div>
            <a class="co-credits" href="#/paywall" aria-label="Get credits">${icon('toll', { fill: true })} ${getBalance() ?? '–'} <span>+</span></a>
          </header>
          ${heroHtml()}
          ${step(1, 'Upload photo')}
          ${photoHtml()}
          ${step(2, 'Choose enhance type')}
          ${typesHtml()}
          ${sizeHtml()}
          <footer class="co-foot" id="co-foot">${footerHtml()}</footer>
          <input type="file" id="co-file" accept="${ACCEPTED_TYPES.join(',')}" hidden />
        </div>`;
    };

    const paintFooter = (): void => {
      const f = root.querySelector<HTMLElement>('#co-foot');
      if (f) f.innerHTML = footerHtml();
    };

    // -- Upload -----------------------------------------------------------

    const upload = async (file: File): Promise<void> => {
      if (!requireSignIn('Sign in to add your photo.')) return;
      if (!ACCEPTED_TYPES.includes(file.type)) {
        toast('Please choose a JPEG, PNG or WebP image.');
        return;
      }
      if (file.size > CLIENT_MAX_BYTES) {
        toast('That image is too large. Please pick one under 25 MB.');
        return;
      }
      if (photo.previewUrl?.startsWith('blob:')) URL.revokeObjectURL(photo.previewUrl);
      photo = { status: 'uploading', progress: 0, previewUrl: URL.createObjectURL(file), fileId: null, bytes: file.size, error: null };
      const mine = photo;
      paint();
      try {
        const req = await api.requestUpload({
          purpose: 'generation_input',
          contentType: file.type,
          contentLength: file.size,
        });
        await api.putUploadBytes(req.uploadUrl, file, req.uploadHeaders, (r) => {
          if (!alive() || photo !== mine) return;
          mine.progress = r;
          const bar = root.querySelector<HTMLElement>('.co-bar span');
          if (bar) bar.style.width = `${Math.round(r * 100)}%`;
          const label = root.querySelector<HTMLElement>('[data-photo-label]');
          if (label) label.textContent = `Uploading… ${Math.round(r * 100)}%`;
        });
        const meta: UploadFileMeta = await api.completeUpload(req.fileId);
        if (!alive() || photo !== mine) return;
        if (meta.status !== 'ready') throw new Error(meta.error || 'Image processing failed.');
        mine.status = 'ready';
        mine.fileId = meta.id;
        mine.previewUrl = meta.thumbnails?.thumb256 ?? meta.url ?? mine.previewUrl;
      } catch (err) {
        if (!alive() || photo !== mine) return;
        mine.status = 'error';
        mine.error = err instanceof Error ? err.message : 'Upload failed.';
        toastApiError(err, 'Upload failed. Please try again.');
      }
      paint();
    };

    // -- Generate ---------------------------------------------------------

    const generate = async (): Promise<void> => {
      if (generating) return;
      if (!requireAccount('Sign in to generate.')) return;
      const why = missing();
      if (why) {
        toast(why === 'Photo too large for HD Upscale' ? 'HD Upscale takes photos up to 10 MB.' : why);
        // No photo yet: open the picker right away instead of only saying so.
        if (/^Add /.test(why)) root.querySelector<HTMLElement>('[data-act="pick"]')?.click();
        return;
      }
      const c = price();
      let bal = getBalance();
      if (bal === null) bal = await refreshCredits().catch(() => null);
      if (!alive()) return;
      if (bal !== null && bal < c) {
        navigate('/paywall');
        return;
      }
      generating = true;
      paintFooter();
      try {
        const { job } = await api.submitJob({
          tool: TOOL,
          inputFileIds: photo.fileId ? [photo.fileId] : [],
          options: options(),
          idempotencyKey: newId(),
        });
        void refreshCredits().catch(() => undefined);
        navigate(`/result/${job.id}`);
      } catch (err) {
        if (!alive()) return;
        if (err instanceof ApiError && (err.status === 402 || /insufficient|credit/i.test(err.code))) {
          toast('Not enough credits.');
          navigate('/paywall');
        } else {
          toastApiError(err, 'Could not start generation. Please try again.');
        }
      } finally {
        if (alive()) {
          generating = false;
          paintFooter();
        }
      }
    };

    // -- Events (delegated) -----------------------------------------------

    root.onclick = (e) => {
      const el = (e.target as HTMLElement).closest<HTMLElement>('[data-act],[data-type],[data-factor],[data-sample]');
      if (!el) return;
      const d = el.dataset;
      if (d.act === 'back') {
        if (history.length > 1) history.back();
        else navigate('/');
      } else if (d.act === 'pick') {
        if (requireSignIn('Sign in to add your photo.')) {
          withUploadGuide('photo_enhance', () => root.querySelector<HTMLInputElement>('#co-file')?.click());
        }
      } else if (d.act === 'generate') void generate();
      else if (d.act === 'buy') navigate('/paywall');
      else if (d.type) {
        type = (TYPES.find((t) => t.id === d.type)?.id ?? type);
        paint();
      } else if (d.factor) {
        factor = d.factor === '4' ? 4 : 2;
        paint();
      } else if (d.sample) {
        const s = SAMPLE_PHOTOS[Number(d.sample)];
        if (s) {
          void fetch(s.url)
            .then((r) => r.blob())
            .then((b) => upload(new File([b], 'sample.webp', { type: b.type || 'image/webp' })))
            .catch(() => toast("Couldn't load that sample photo."));
        }
      }
    };

    root.onchange = (e) => {
      const input = e.target as HTMLInputElement;
      if (input.id !== 'co-file') return;
      const f = input.files?.[0];
      input.value = '';
      if (f) void upload(f);
    };

    const unsub = creditsChanged.on(() => {
      if (alive()) paintFooter();
    });
    prevCleanup = () => {
      unsub();
      if (photo.previewUrl?.startsWith('blob:')) URL.revokeObjectURL(photo.previewUrl);
    };

    paint();
  };

  void load();
}
